import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { InfoBox } from '../../../../../InfoBox';
import { Dialog } from '../../../../../Dialog/Dialog';
import { Button } from '../../../../../Button/Button';
import {
  OnQuickReportsChanged,
  OnVariablesChanged,
  QueryModel,
  SortModel
} from '../../../../utils/DataTableQueryProvider/DataTableQuery.types';
import { Typography } from '../../../../../Typography/Typography';
import {
  FileFormat,
  QuickReportOrigin
} from '../../../../DataTableAdvancedElements/DataTableQuickReports/utils/QuickReports.type';
import { AppForm } from '../../../../../Form/AppForm';
import { EditScheduleDesc } from '../panel/EditScheduleDesc';
import { useIntlUds } from '../../../../../../translations/UdsIntlProvider';
import { useTranslatedFileFormatOptions } from '../../../../DataTableAdvancedElements/DataTableQuickReports/utils/useTranslatedOptions';

interface Props<Q extends QueryModel, S extends SortModel> {
  switchToScheduleExport: () => void;
  report: OnQuickReportsChanged<Q, S> | OnVariablesChanged<Q, S> | undefined;
  exportFileFormats: FileFormat[];
  source: QuickReportOrigin;
  onClose: () => void;
  onSave:
    | ((
        quickReport: OnQuickReportsChanged<Q, S> | OnVariablesChanged<Q, S>,
        fileFormat: FileFormat,
        source: QuickReportOrigin,
        quickReportName: string | undefined
      ) => void)
    | undefined;
  children?: React.ReactNode;
}

export const ExportDialog = <Q extends QueryModel, S extends SortModel>({
  exportFileFormats,
  report,
  onClose,
  onSave,
  switchToScheduleExport,
  source,
  children
}: Props<Q, S>) => {
  const { intl } = useIntlUds();
  const [fileFormat, setFileFormat] = useState<{ fileFormat: FileFormat }>({ fileFormat: 'CSV' });
  const filteredOptions = useTranslatedFileFormatOptions(exportFileFormats);
  const handleOnSave = useCallback(async () => {
    if (!report) return;
    const reportName = (report as OnQuickReportsChanged<Q, S>)?.name ?? '';
    onSave?.(report, fileFormat.fileFormat, source, reportName);
    onClose();
  }, [report, onSave, fileFormat.fileFormat, source, onClose]);

  const handleSwitchToScheduleExport = useCallback(() => {
    onClose();
    switchToScheduleExport();
  }, [onClose, switchToScheduleExport]);

  const quickReport = report as OnQuickReportsChanged<Q, S>;

  return (
    <Dialog
      dataTest="quick-report-export-dialog"
      title={intl.DataTable.ExportDialog.label}
      open={!!report}
      onClose={onClose}
      showCancelButton
      actions={[
        <Button key="export-submit-button" dataTest="submit" variant="contained" color="primary" onClick={handleOnSave}>
          {intl.DataTable.ExportDialog.label}
        </Button>
      ]}
    >
      {quickReport?.name && (
        <StyledMessage>
          <StyledTypography>
            {intl.DataTable.ExportDialog.withQuickView.message}
            <br />
            <b>{quickReport.name}</b>
          </StyledTypography>
          {!quickReport.schedule && (
            <InfoBox
              icon="calendar_today"
              type="INFO"
              title={intl.DataTable.ExportDialog.withQuickView.noSchedule.title}
            >
              {intl.DataTable.ExportDialog.withQuickView.noSchedule.message}
              <StyledButtonWrapper>
                <Button
                  color="primary"
                  onClick={handleSwitchToScheduleExport}
                  variant="text"
                  dataTest="switch-to-schedule-export-button"
                >
                  {intl.DataTable.ExportDialog.withQuickView.noSchedule.btn}
                </Button>
              </StyledButtonWrapper>
            </InfoBox>
          )}
          {quickReport.schedule && (
            <InfoBox
              icon="calendar_today"
              type="INFO"
              title={intl.DataTable.ExportDialog.withQuickView.scheduled.title}
            >
              <EditScheduleDesc report={quickReport} />
              <StyledButtonWrapper>
                <Button
                  color="primary"
                  onClick={handleSwitchToScheduleExport}
                  variant="text"
                  dataTest="edit-schedule-button"
                >
                  {intl.DataTable.ExportDialog.withQuickView.scheduled.editBtn}
                </Button>
              </StyledButtonWrapper>
            </InfoBox>
          )}
        </StyledMessage>
      )}
      <AppForm initialValues={{ fileFormat: 'CSV' }} onChange={setFileFormat}>
        <AppForm.Control name="fileFormat" marginBottom={0}>
          <AppForm.Label>{intl.DataTable.ExportDialog.fileFormatInput.label}</AppForm.Label>
          <AppForm.Select disableClearable options={filteredOptions} />
        </AppForm.Control>
      </AppForm>
      {children}
    </Dialog>
  );
};

const StyledMessage = styled.div`
  padding-bottom: ${({ theme }) => theme.spacingSize.l};
`;

const StyledTypography = styled(Typography)`
  padding-bottom: ${({ theme }) => theme.spacingSize.l};
`;

const StyledButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;
